/**
 * "There's a newer Chilli Koala — want to grab it?"
 *
 * The native shells bundle their own copy of `dist/`, so a web deploy never
 * reaches an installed app: someone who installed in March is still running
 * March's code, including March's bugs, until they visit a store. Nothing in
 * the app told them that, so this asks the live site what the current release
 * is and says so.
 *
 * The published manifest is the single source of truth
 * (`public/app-version.json`, served from chillikoala.app), which means the
 * store links and the "too old to keep running" floor can be changed by
 * redeploying the website — no new binary, which is exactly the constraint
 * that makes this feature necessary in the first place.
 */

/** Where the live manifest lives, for builds that ship their own copy. */
export const REMOTE_MANIFEST = "https://chillikoala.app/app-version.json";

/** Play derives from the package id, so it needs no server round trip. */
export const PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.joepong.chillikoala";

/**
 * A store link only ever points at a store. The manifest is our own file on
 * our own domain, but it is still remote input that ends in "open this URL",
 * so the destination is checked rather than trusted.
 */
const STORE_HOSTS = [
  "apps.apple.com",
  "itunes.apple.com",
  "play.google.com",
];

export interface VersionManifest {
  /** The newest release in the stores. */
  latest: string;
  /** Below this, the app asks rather than offers. Optional. */
  minSupported?: string;
  /** One kid-readable line about what's new. Optional. */
  notes?: string;
  iosUrl?: string;
  androidUrl?: string;
}

export type UpdateStatus = "current" | "optional" | "required";

/**
 * Compare two dotted versions numerically. "1.10.0" is newer than "1.9.0";
 * a string compare gets that backwards, which is the whole reason this
 * exists rather than `a < b`.
 */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
};

/**
 * Running *ahead* of the manifest is normal, not an error: that is every
 * TestFlight build, and every local dev run between a version bump and the
 * deploy that follows it. It reports "current" and says nothing.
 */
export const updateStatus = (
  current: string,
  manifest: VersionManifest | null,
): UpdateStatus => {
  if (!manifest?.latest) return "current";
  if (compareVersions(current, manifest.latest) >= 0) return "current";
  if (manifest.minSupported && compareVersions(current, manifest.minSupported) < 0) {
    return "required";
  }
  return "optional";
};

const isStoreUrl = (url: string | undefined): url is string => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return (
      (u.protocol === "https:" || u.protocol === "itms-apps:") &&
      STORE_HOSTS.includes(u.hostname)
    );
  } catch {
    return false;
  }
};

/**
 * Where the "Update" button should send someone.
 *
 * On the web there is no store — the newest code is one reload away — so this
 * returns null and the dialog offers a reload instead.
 */
export const storeUrlFor = (
  platform: string,
  manifest: VersionManifest | null,
): string | null => {
  if (platform === "ios") {
    return isStoreUrl(manifest?.iosUrl) ? manifest.iosUrl : null;
  }
  if (platform === "android") {
    return isStoreUrl(manifest?.androidUrl) ? manifest.androidUrl : PLAY_URL;
  }
  return null;
};

/* ───────────────────────────── snoozing ───────────────────────────── */

const SNOOZE_KEY = "chillikoala.updateSnooze";
export const SNOOZE_MS = 24 * 60 * 60 * 1000;

/**
 * Asking once a day is a reminder; asking every launch is nagging, and this
 * app is used by children several times a day. A required update ignores the
 * snooze — at that point the app genuinely cannot be trusted to work.
 */
export const isSnoozed = (
  version: string,
  now: number = Date.now(),
  read: () => string | null = () => {
    try {
      return localStorage.getItem(SNOOZE_KEY);
    } catch {
      return null;
    }
  },
): boolean => {
  const raw = read();
  if (!raw) return false;
  try {
    const { version: v, at } = JSON.parse(raw) as { version: string; at: number };
    return v === version && now - at < SNOOZE_MS;
  } catch {
    return false;
  }
};

export const snooze = (version: string, now: number = Date.now()): void => {
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify({ version, at: now }));
  } catch {
    // Private mode or a full quota — worst case we ask again next launch.
  }
};

/* ───────────────────────────── fetching ───────────────────────────── */

/**
 * Native builds must ask the live site; a web build asks its own origin, so
 * a preview deploy checks itself instead of reporting the production version.
 */
export const manifestUrl = (isNative: boolean): string =>
  isNative ? REMOTE_MANIFEST : "/app-version.json";

export const fetchManifest = async (
  isNative: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<VersionManifest | null> => {
  try {
    const res = await fetchImpl(`${manifestUrl(isNative)}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionManifest;
    return typeof data?.latest === "string" ? data : null;
  } catch {
    // Offline, or the site is down. An update reminder is the last thing that
    // should interrupt a kid mid-lesson, so failure is silent by design.
    return null;
  }
};
